/* METTA — marks the collection filter bar while it is pinned (.is-stuck) and
   publishes its pinned offset as --metta-bar-top, so the CSS can give the bar
   a solid ground that reaches up to the top of the viewport. Without it the
   floating header and the translucent bar let the grid show through both,
   and through the gap between them. */
(() => {
  const bar = document.querySelector(':is(.template-collection, .template-search) form.filter-bar.filter-bar--sticky');
  if (!bar) return;

  // A copy of the department mini-nav rides in the bar, shown only while
  // pinned (CSS), so the categories stay one click away down the grid.
  const subnav = document.querySelector('.metta-subnav');
  if (subnav && !bar.querySelector('.metta-subnav--pinned')) {
    const copy = subnav.cloneNode(true);
    copy.classList.add('metta-subnav--pinned');
    copy.setAttribute('aria-hidden', 'true');
    copy.querySelectorAll('a').forEach((a) => a.setAttribute('tabindex', '-1'));
    bar.prepend(copy);
  }

  // Pinned, the bar slides down by the mini-nav's height (CSS `translate`),
  // so its own rect can't tell whether it is stuck — that would flip back
  // and forth. A zero-height marker just above it scrolls with the page and
  // reaches the pinned line exactly when the bar does.
  const marker = document.createElement('div');
  marker.setAttribute('aria-hidden', 'true');
  bar.before(marker);
  const pinnedRow = bar.querySelector('.metta-subnav--pinned');

  const update = () => {
    const pinnedTop = parseFloat(getComputedStyle(bar).top) || 0;
    const stuck = bar.offsetParent !== null && marker.getBoundingClientRect().top <= pinnedTop + 1 && window.scrollY > 0;
    bar.classList.toggle('is-stuck', stuck);
    // The header squares its bottom corners onto the pinned bar (CSS).
    document.documentElement.classList.toggle('metta-filters-pinned', stuck);
    bar.style.setProperty('--metta-bar-top', `${pinnedTop}px`);
    if (pinnedRow) bar.style.setProperty('--metta-subnav-pinned-h', `${pinnedRow.offsetHeight}px`);
  };
  // A few reads and a class toggle: cheap enough to run on every scroll event.
  window.addEventListener('scroll', update, { passive: true });
  window.addEventListener('resize', update);
  update();
})();

/* Fit open dropdown lists to the space below their button. Keep the header
   visible and end on a whole option row, instead of the old fixed 420px
   panel cutting off the final option or extending below the viewport. */
(() => {
  const bar = document.querySelector('form.filter-bar');
  if (!bar) return;
  let frame;

  const fitLists = () => {
    frame = null;
    bar.querySelectorAll('.filter-group__values[aria-hidden="false"]').forEach((panel) => {
      const list = panel.querySelector('.filter-group__list');
      if (!list || !panel.getClientRects().length) return;
      const items = Array.from(list.children);
      if (!items.length) return;
      const style = getComputedStyle(list);
      const padding = parseFloat(style.paddingTop) + parseFloat(style.paddingBottom);
      const first = items[0].getBoundingClientRect();
      const nextRow = items.find((item) => item.getBoundingClientRect().top > first.top + 1);
      const pitch = nextRow ? nextRow.getBoundingClientRect().top - first.top : first.height;
      if (!pitch) return;
      const gap = pitch - first.height;
      const header = panel.querySelector('.filter-group__values-header');
      const available = window.innerHeight - panel.getBoundingClientRect().top - 16
        - (header ? header.getBoundingClientRect().height : 0) - 2;
      const rows = Math.max(1, Math.floor((available - padding + gap) / pitch));
      list.style.setProperty('--metta-filter-list-height', `${rows * pitch - gap + padding}px`);
    });
  };

  const schedule = () => {
    if (frame == null) frame = requestAnimationFrame(fitLists);
  };
  // The theme replaces filter controls after a selection and toggles their
  // aria-hidden state on open; one observer also covers those new controls.
  new MutationObserver(schedule).observe(bar, {
    subtree: true, childList: true, attributes: true, attributeFilter: ['aria-hidden'],
  });
  window.addEventListener('resize', schedule);
  window.addEventListener('scroll', schedule, { passive: true });
  schedule();
})();

/* "+ Sía" on desktop opens the filter drawer through the drawer's own toggle
   (the phone's Sía button, hidden from 960). The bar is re-rendered on every
   filter change, so the click is delegated rather than bound.
   4 Nov 2026 (Björn): Flokka belongs under Sía. "Flokka" (bar or phone)
   opens the same drawer with its Flokka group open, at the top; "Sía"
   opens it with that group closed, so the filters come first. The theme
   opens the drawer on the button's own click; the group is set just after,
   through its own toggle, so the theme's slide and state stay in charge. */
(() => {
  if (!document.querySelector('.metta-filter-open, [data-metta-sort-open]')) return;

  const sortGroup = (open) => {
    const toggle = document.querySelector('[data-filter-drawer] [data-drawer-group-toggle="drawer-group--sort"]');
    if (!toggle) return;
    if ((toggle.getAttribute('aria-expanded') === 'true') !== open) toggle.click();
    if (open) toggle.closest('[data-scroll-lock-ignore]')?.scrollTo({ top: 0 });
  };

  document.addEventListener('click', (event) => {
    const filters = event.target.closest('.metta-filter-open');
    const sort = event.target.closest('.metta-sort-open');
    if (filters || sort) {
      const toggle = document.querySelector(sort ? '[data-metta-sort-open]' : '[data-mobile-filters-toggle]');
      if (!toggle) return;
      event.preventDefault();
      toggle.click();
      return;
    }
    if (event.target.closest('[data-metta-sort-open]')) sortGroup(true);
    else if (event.target.closest('[data-mobile-filters-toggle]')) sortGroup(false);
  });
})();

/* Phones and tablets: marks the "+ Sía / ⇅ Flokka" bar while it is pinned
   under the header (.is-stuck, and html.metta-filters-pinned for the
   header), so CSS can paint it as the floating header's lower half. Same
   zero-height marker as the desktop bar: the bar's own rect can't tell. */
(() => {
  const bar = document.querySelector('[data-mobile-filters].mobile-filters--sticky');
  if (!bar) return;

  const marker = document.createElement('div');
  marker.setAttribute('aria-hidden', 'true');
  bar.before(marker);

  const update = () => {
    // Hidden from 960, where the desktop bar owns the page class.
    if (bar.offsetParent === null) {
      bar.classList.remove('is-stuck');
      return;
    }
    const pinnedTop = parseFloat(getComputedStyle(bar).top) || 0;
    const stuck = marker.getBoundingClientRect().top <= pinnedTop + 1 && window.scrollY > 0;
    bar.classList.toggle('is-stuck', stuck);
    document.documentElement.classList.toggle('metta-filters-pinned', stuck);
    bar.style.setProperty('--metta-bar-top', `${pinnedTop}px`);
  };
  window.addEventListener('scroll', update, { passive: true });
  window.addEventListener('resize', update);
  update();
})();
